import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { isAuthRetryableFetchError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/cadastro")({
  head: () => ({
    meta: [
      { title: "Criar Conta — Loja EG Elétrica" },
      { name: "description", content: "Crie sua conta para acompanhar seus pedidos." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CadastroPage,
});

function CadastroPage() {
  const navigate = useNavigate();
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [resendError, setResendError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setError("A senha precisa ter pelo menos 8 caracteres.");
      return;
    }
    setSubmitting(true);
    setError("");
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: nome },
        emailRedirectTo: `${window.location.origin}/confirmar-email`,
      },
    });
    setSubmitting(false);
    if (error) {
      // Mesmo raciocínio de login.tsx/recuperar-senha.tsx: falha de
      // transporte/config não é um problema com os dados informados.
      setError(
        isAuthRetryableFetchError(error)
          ? "Tivemos um problema técnico e não conseguimos processar seu cadastro agora. Tente novamente em alguns minutos."
          : error.message === "User already registered"
            ? "Este e-mail já está cadastrado."
            : "Não foi possível criar sua conta. Tente novamente.",
      );
      return;
    }
    if (data.session) {
      navigate({ to: "/" });
      return;
    }
    setAwaitingConfirmation(true);
  };

  const handleResend = async () => {
    setResending(true);
    setResendError("");
    setResendMessage("");
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: `${window.location.origin}/confirmar-email` },
    });
    setResending(false);
    if (error) {
      setResendError(
        isAuthRetryableFetchError(error)
          ? "Tivemos um problema técnico e não conseguimos reenviar agora. Tente novamente em alguns minutos."
          : "Não foi possível reenviar o e-mail agora. Tente novamente em instantes.",
      );
      return;
    }
    setResendMessage("Enviamos um novo e-mail de confirmação.");
  };

  if (awaitingConfirmation) {
    return (
      <div className="mx-auto max-w-sm px-4 py-12 text-center">
        <h1 className="font-display text-2xl font-bold text-eg-dark mb-3">Quase lá!</h1>
        <p className="text-sm text-muted-foreground mb-4">
          Enviamos um e-mail de confirmação para <strong>{email}</strong>. Clique no link para
          ativar sua conta.
        </p>
        {resendError && <p className="mb-2 text-xs text-red-600">{resendError}</p>}
        {resendMessage ? (
          <p className="text-xs text-eg-goldtext">{resendMessage}</p>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="text-sm font-medium text-eg-goldtext hover:underline disabled:opacity-60"
          >
            {resending ? "Reenviando..." : "Não recebeu? Reenviar e-mail"}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-sm px-4 py-12">
      <h1 className="font-display text-2xl font-bold text-eg-dark mb-6">Criar Conta</h1>
      <form
        onSubmit={handleSubmit}
        className="space-y-4 rounded-lg bg-white border border-border p-6"
      >
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-eg-dark">Nome completo</span>
          <input
            required
            className="input"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-eg-dark">E-mail</span>
          <input
            type="email"
            required
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-eg-dark">Senha</span>
          <input
            type="password"
            required
            minLength={8}
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        {error && <p className="text-xs text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-eg-gold px-4 py-2.5 font-semibold text-eg-dark hover:bg-eg-goldhover disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <UserPlus className="h-5 w-5" />
          )}
          Criar conta
        </button>
        <div className="text-center text-xs text-muted-foreground">
          Já tem conta?{" "}
          <Link to="/login" className="hover:text-eg-gold transition-colors">
            Entrar
          </Link>
        </div>
      </form>

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.375rem;
          border: 1px solid var(--border);
          background: white;
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .input:focus {
          border-color: var(--eg-gold);
          box-shadow: 0 0 0 2px color-mix(in oklab, var(--eg-gold) 30%, transparent);
        }
      `}</style>
    </div>
  );
}
